import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Card,
  CardContent,
  Stack,
  Fade,
  Alert,
  Tab,
  Tabs,
  CardActionArea,
  useTheme
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import EmailIcon from '@mui/icons-material/Email';
import toast from 'react-hot-toast';

// Interface atualizada para corresponder ao backend
interface EmployeeData {
  servidor: {
    id: number;
    nome_completo: string;
    cpf: string;
    email?: string;
    telefone?: string;
  };
  matriculas: Array<{
    id: number;
    matricula: string;
    salario_julho_07: number;
    valor_total_2007: number;
    salario_julho_08: number;
    valor_total_2008: number;
    salario_julho_09: number;
    valor_total_2009: number;
    salario_julho_10: number;
    valor_total_2010: number;
    total_geral: number;
    honorarios_adv_25: number;
    igeprev_11: number;
    valor_liquido_servidor: number;
  }>;
  totais: {
    total_geral: number;
    total_honorarios: number;
    total_igeprev: number;
    total_liquido: number;
  };
}

interface EmployeeDetailsProps {
  data: EmployeeData;
  onRequestDocument: () => void;
}

const EmployeeDetails: React.FC<EmployeeDetailsProps> = ({ 
  data, 
  onRequestDocument 
}) => {
  const [selectedMatriculaIndex, setSelectedMatriculaIndex] = useState(0);
  const theme = useTheme();
  
  // Debug: verifique a estrutura de dados
  console.log('Dados recebidos no EmployeeDetails:', data);
  console.log('Totais do backend:', data.totais);
  console.log('Matrículas:', data.matriculas);
  
  // Matrícula selecionada
  const selectedMatricula = data.matriculas && data.matriculas.length > 0 
    ? data.matriculas[selectedMatriculaIndex]
    : null;

  const handleMatriculaChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedMatriculaIndex(newValue);
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null || isNaN(value)) {
      return 'R$ 0,00';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Função para calcular totais manualmente - CORRIGIDA
  const calcularTotaisManual = () => {
    if (!data.matriculas || data.matriculas.length === 0) {
      return { 
        total_geral: 0, 
        total_honorarios: 0, 
        total_igeprev: 0, 
        total_liquido: 0 
      };
    }
    
    // Usar parseFloat para garantir que são números
    const totalGeral = data.matriculas.reduce((sum, mat) => {
      const valor = parseFloat(mat.total_geral?.toString() || '0');
      console.log(`Matrícula ${mat.matricula}: total_geral = ${valor}`);
      return sum + valor;
    }, 0);
    
    const totalHonorarios = data.matriculas.reduce((sum, mat) => {
      const valor = parseFloat(mat.honorarios_adv_25?.toString() || '0');
      return sum + valor;
    }, 0);
    
    const totalIgeprev = data.matriculas.reduce((sum, mat) => {
      const valor = parseFloat(mat.igeprev_11?.toString() || '0');
      return sum + valor;
    }, 0);
    
    const totalLiquido = data.matriculas.reduce((sum, mat) => {
      const valor = parseFloat(mat.valor_liquido_servidor?.toString() || '0');
      return sum + valor;
    }, 0);
    
    console.log('Totais manuais calculados:', { 
      totalGeral, 
      totalHonorarios, 
      totalIgeprev, 
      totalLiquido 
    });
    
    return {
      total_geral: totalGeral,
      total_honorarios: totalHonorarios,
      total_igeprev: totalIgeprev,
      total_liquido: totalLiquido
    };
  };

  // CALCULAR OS TOTAIS - função corrigida
  const calcularSomasCorretas = () => {
    const totaisManuais = calcularTotaisManual();
    
    // Se o backend retornou valores, usar eles, senão usar os calculados manualmente
    const temTotaisBackend = data.totais && 
      (data.totais.total_geral > 0 || 
       data.totais.total_honorarios > 0 || 
       data.totais.total_igeprev > 0 || 
       data.totais.total_liquido > 0);
    
    const totaisFinais = temTotaisBackend ? data.totais : totaisManuais;
    
    console.log('Totais finais a serem exibidos:', totaisFinais);
    console.log('Backend tem totais?', temTotaisBackend);
    
    return totaisFinais;
  };

  const totaisExibicao = calcularSomasCorretas();

  // Preparar dados por ano para a matrícula selecionada
  const yearsData = selectedMatricula ? [
    { 
      year: 2007, 
      salary: selectedMatricula.salario_julho_07, 
      total: selectedMatricula.valor_total_2007 
    },
    { 
      year: 2008, 
      salary: selectedMatricula.salario_julho_08, 
      total: selectedMatricula.valor_total_2008 
    },
    { 
      year: 2009, 
      salary: selectedMatricula.salario_julho_09, 
      total: selectedMatricula.valor_total_2009 
    },
    { 
      year: 2010, 
      salary: selectedMatricula.salario_julho_10, 
      total: selectedMatricula.valor_total_2010 
    },
  ] : [];

  // Se não houver matrículas, mostrar mensagem
  if (!selectedMatricula) {
    return (
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 3,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Nenhuma matrícula encontrada
        </Typography>
        <Typography variant="body1" color="text.secondary">
          O servidor foi encontrado, mas não há matrículas cadastradas.
        </Typography>
      </Paper>
    );
  }

  return (
    <Fade in={true} timeout={700}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, md: 4 }, 
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid',
          borderColor: 'grey.300',
          animation: 'fadeIn 0.7s ease-out',
        }}
      >
        {/* Cabeçalho */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Chip 
            label="DEMONSTRATIVO OFICIAL" 
            color="primary" 
            sx={{ 
              mb: 2, 
              fontWeight: 'bold',
              fontSize: '0.9rem',
              padding: '4px 12px'
            }}
          />
          <Typography variant="h4" color="primary.main" gutterBottom fontWeight="bold">
            DEMONSTRATIVO DE CÁLCULO
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600}>
            Apuração de Diferença Salarial - Anos 2007 a 2010
          </Typography>
        </Box>

        {/* Informações do Servidor */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Autor
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {data.servidor.nome_completo}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  CPF
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {data.servidor.cpf}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total de Matrículas
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {data.matriculas.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Seletor de Matrículas (se tiver mais de uma) */}
        {data.matriculas.length > 1 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom color="primary.main" fontWeight="bold">
              Selecione a Matrícula para Visualizar
            </Typography>
            <Tabs 
              value={selectedMatriculaIndex} 
              onChange={handleMatriculaChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                mb: 2,
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: 3
                }
              }}
            >
              {data.matriculas.map((matricula, index) => (
                <Tab 
                  key={matricula.id}
                  label={`Matrícula ${matricula.matricula}`}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    color: index === selectedMatriculaIndex ? 'primary.main' : 'text.secondary',
                    borderBottom: index === selectedMatriculaIndex ? '3px solid' : 'none',
                    borderColor: 'primary.main',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 'bold'
                    }
                  }}
                />
              ))}
            </Tabs>
            
            {/* Cards com preview das matrículas */}
            <Grid container spacing={2}>
              {data.matriculas.map((matricula, index) => (
                <Grid item xs={12} sm={6} md={3} key={matricula.id}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      borderRadius: 2,
                      border: index === selectedMatriculaIndex ? '2px solid' : '1px solid',
                      borderColor: index === selectedMatriculaIndex ? 'primary.main' : 'grey.300',
                      backgroundColor: index === selectedMatriculaIndex ? 'primary.50' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                        backgroundColor: 'primary.50'
                      }
                    }}
                    onClick={() => setSelectedMatriculaIndex(index)}
                  >
                    <CardActionArea sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: index === selectedMatriculaIndex ? 'primary.main' : 'grey.400',
                            mr: 1
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Matrícula
                        </Typography>
                      </Box>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color={index === selectedMatriculaIndex ? 'primary.main' : 'text.primary'}
                        sx={{ mb: 1 }}
                      >
                        {matricula.matricula}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color={index === selectedMatriculaIndex ? 'primary.main' : 'text.secondary'}
                        sx={{ 
                          display: 'block',
                          fontWeight: index === selectedMatriculaIndex ? 'bold' : 'normal'
                        }}
                      >
                        Líquido: {formatCurrency(matricula.valor_liquido_servidor)}
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Informações da Matrícula Selecionada */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chip 
              label={`MATRÍCULA ${selectedMatricula.matricula}`} 
              color="primary" 
              size="small" 
              sx={{ 
                mr: 1,
                fontWeight: 'bold',
                backgroundColor: 'primary.main',
                color: 'white'
              }} 
            />
            <Typography variant="h6" color="primary.main" fontWeight="bold">
              Detalhes da Matrícula Selecionada
            </Typography>
          </Box>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Valor Total desta Matrícula
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {formatCurrency(selectedMatricula.total_geral)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Antes dos descontos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ 
                borderRadius: 2, 
                height: '100%', 
                border: '2px solid',
                borderColor: 'success.main',
                backgroundColor: 'success.50'
              }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Líquido desta Matrícula
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {formatCurrency(selectedMatricula.valor_liquido_servidor)}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    Disponível para recebimento
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Detalhamento por Ano da Matrícula Selecionada */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chip 
              label="DETALHAMENTO POR ANO" 
              color="secondary" 
              size="small" 
              sx={{ 
                mr: 1,
                fontWeight: 'bold',
                backgroundColor: 'secondary.main',
                color: 'white'
              }} 
            />
            <Typography variant="h6" color="primary.main" fontWeight="bold">
              Matrícula {selectedMatricula.matricula}
            </Typography>
          </Box>
          
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ano</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Salário Julho</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Valor Total</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {yearsData.map((yearData, index) => (
                  <TableRow 
                    key={yearData.year}
                    sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                      '&:hover': { backgroundColor: 'grey.100' }
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="bold" color="primary.main">
                        {yearData.year}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="text.secondary">
                        {formatCurrency(yearData.salary)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary.main">
                        {formatCurrency(yearData.total)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={yearData.total > 0 ? "Devido" : "Sem débito"} 
                        size="small" 
                        color={yearData.total > 0 ? "warning" : "default"}
                        variant="outlined"
                        sx={{ 
                          fontWeight: 'bold',
                          borderWidth: '2px'
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Descontos da Matrícula Selecionada */}
        <Box sx={{ mb: 4, p: 3, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.300' }}>
          <Typography variant="h6" gutterBottom color="text.primary" fontWeight="bold">
            Descontos Aplicados - Matrícula {selectedMatricula.matricula}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Honorários Advocatícios
                </Typography>
                <Typography variant="h6" color="error" fontWeight="bold">
                  - {formatCurrency(selectedMatricula.honorarios_adv_25)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  25% sobre o total
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  IGEPREV
                </Typography>
                <Typography variant="h6" color="error" fontWeight="bold">
                  - {formatCurrency(selectedMatricula.igeprev_11)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  11% sobre o total
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ 
                p: 2, 
                borderRadius: 2, 
                height: '100%',
                border: '2px solid',
                borderColor: 'success.main',
                backgroundColor: 'success.50'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Valor Líquido
                </Typography>
                <Typography variant="h5" color="success.main" fontWeight="bold">
                  {formatCurrency(selectedMatricula.valor_liquido_servidor)}
                </Typography>
                <Typography variant="caption" color="success.main">
                  Desta matrícula
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Resumo de TODAS as Matrículas (se tiver mais de uma) - CORRIGIDO */}
        {data.matriculas.length > 1 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Chip 
                label="RESUMO GERAL" 
                color="primary" 
                size="small" 
                sx={{ 
                  mr: 1,
                  fontWeight: 'bold',
                  backgroundColor: 'primary.main',
                  color: 'white'
                }} 
              />
              <Typography variant="h6" color="primary.main" fontWeight="bold">
                Todas as Matrículas
              </Typography>
            </Box>
            
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Matrícula</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Valor Total</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Honorários (25%)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>IGEPREV (11%)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Líquido</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.matriculas.map((matricula) => (
                    <TableRow 
                      key={matricula.id}
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                        '&:hover': { backgroundColor: 'grey.100' },
                        backgroundColor: matricula.id === selectedMatricula.id ? 'primary.50' : 'transparent'
                      }}
                    >
                      <TableCell>
                        <Typography 
                          fontWeight="bold" 
                          color={matricula.id === selectedMatricula.id ? 'primary.main' : 'text.primary'}
                        >
                          {matricula.matricula}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="primary.main">
                          {formatCurrency(matricula.total_geral)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="error" fontWeight="bold">
                          - {formatCurrency(matricula.honorarios_adv_25)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="error" fontWeight="bold">
                          - {formatCurrency(matricula.igeprev_11)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="success.main">
                          {formatCurrency(matricula.valor_liquido_servidor)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Linha de totais - AGORA USANDO OS TOTAIS CORRETOS */}
                  <TableRow sx={{ 
                    backgroundColor: 'primary.dark',
                    '& .MuiTableCell-root': {
                      borderColor: 'primary.main'
                    }
                  }}>
                    <TableCell>
                      <Typography fontWeight="bold" color="white">
                        TOTAL
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="white">
                        {formatCurrency(totaisExibicao.total_geral)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="white">
                        - {formatCurrency(totaisExibicao.total_honorarios)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="white">
                        - {formatCurrency(totaisExibicao.total_igeprev)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h6" fontWeight="bold" color="secondary.light">
                        {formatCurrency(totaisExibicao.total_liquido)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Resumo de Valores TOTAIS (soma de todas as matrículas) - AGORA FUNCIONANDO */}
        <Box sx={{ 
          mb: 4, 
          p: 3, 
          backgroundColor: 'primary.dark', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'primary.main'
        }}>
          <Typography variant="h6" gutterBottom color="white" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            VALORES TOTAIS (TODAS AS MATRÍCULAS)
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" color="white" gutterBottom sx={{ opacity: 0.9 }}>
                  VALOR TOTAL DEVIDO
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="white">
                  {formatCurrency(totaisExibicao.total_geral)}
                </Typography>
                <Typography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                  Soma de {data.matriculas.length} matrícula(s)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" color="white" gutterBottom sx={{ opacity: 0.9 }}>
                  VALOR LÍQUIDO TOTAL
                </Typography>
                <Typography variant="h3" fontWeight="bold" color="secondary.light">
                  {formatCurrency(totaisExibicao.total_liquido)}
                </Typography>
                <Typography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                  Disponível para recebimento
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          {/* Mostrar breakdown dos totais para debug */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
            <Typography variant="caption" color="white" sx={{ display: 'block', textAlign: 'center' }}>
              Detalhamento: Total ({formatCurrency(totaisExibicao.total_geral)}) - 
              Honorários ({formatCurrency(totaisExibicao.total_honorarios)}) - 
              IGEPREV ({formatCurrency(totaisExibicao.total_igeprev)}) = 
              Líquido ({formatCurrency(totaisExibicao.total_liquido)})
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Ações */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom color="primary.main" fontWeight="bold">
            Próximos Passos
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3 }}>
            Para solicitar o recebimento dos valores, clique no botão abaixo e informe seu e-mail.
            Enviaremos um código de validação para gerar os documentos oficiais.
          </Typography>

          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              backgroundColor: 'info.light',
              border: '1px solid',
              borderColor: 'info.main'
            }}
          >
            <Typography variant="body2" fontWeight="bold" color="info.dark">
              Importante:
            </Typography>
            <Typography variant="body2" color="info.dark">
              O documento será gerado considerando TODAS as matrículas.
              Você receberá um único documento com o valor líquido total de {formatCurrency(totaisExibicao.total_liquido)}
            </Typography>
          </Alert>

          <Button
            variant="contained"
            size="large"
            startIcon={<RequestQuoteIcon />}
            onClick={onRequestDocument}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #1976d2 30%, #0d47a1 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #0d47a1 30%, #002171 90%)',
              }
            }}
          >
            Solicitar Documentos
          </Button>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <EmailIcon color="action" fontSize="small" />
            <Typography variant="caption" color="text.secondary">
              Após a solicitação, você receberá um código de validação no e-mail
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Fade>
  );
};

export default EmployeeDetails;